import { StateCreator } from "zustand";

import { type IacTableDataSlice } from "./types";
import { IacTableStoreState } from "../types";
import { DEFAULT_GROUP_META } from "./constants";

const makeIacTableDataSlice: StateCreator<
  IacTableStoreState,
  [["zustand/immer", never]],
  [["zustand/persist", never]],
  IacTableDataSlice
> = (set, get) => ({
  groups: [],
  groupsLoading: false,
  setGroupsLoading: (groupsLoading) => set({ groupsLoading }),
  setGroups: (groups) =>
    set((state) => {
      state.groups = groups;
    }),
  resources: {},
  resourcesIdsPerGroup: {},
  groupsMeta: {},
  toggleGroupMeta: (groupId) =>
    set((state) => {
      const groupMeta = state.groupsMeta[groupId] || DEFAULT_GROUP_META;
      state.groupsMeta[groupId] = {
        ...groupMeta,
        isOpen: !groupMeta.isOpen,
      };
    }),
  openGroup: (groupId) =>
    set((state) => {
      const groupMeta = state.groupsMeta[groupId] || DEFAULT_GROUP_META;
      state.groupsMeta[groupId] = {
        ...groupMeta,
        isOpen: true,
      };
    }),
  updateGroupMeta: (groupId, meta) =>
    set((state) => {
      const groupMeta = state.groupsMeta[groupId] || DEFAULT_GROUP_META;
      state.groupsMeta[groupId] = { ...groupMeta, ...meta };
    }),
  loadResources: async (queryProps, asyncQuery) => {
    const after = get().groupsMeta[queryProps.groupId]?.endCursor || null;
    const { data, hasMore, endCursor } = await asyncQuery({
      groupId: queryProps.groupId,
      groupBy: queryProps.groupBy,
      parentStackId: queryProps.parentStackId,
      cursor: after,
      rowsPerPage: queryProps.rowsPerPage,
    });
    get().updateGroupMeta(queryProps.groupId, { hasMore, endCursor });

    set((state) => {
      const prevGroupResourceIds = state.resourcesIdsPerGroup[queryProps.groupId] || [];
      const resourcesIds = data.map((resource) => resource.id);

      data.forEach((resource) => {
        state.resources[resource.id] = resource;
      });
      // Use a Set to ensure resource IDs are unique
      state.resourcesIdsPerGroup[queryProps.groupId] = Array.from(
        new Set([...prevGroupResourceIds, ...resourcesIds])
      );
    });
  },
  flushTable: () => {
    set((state) => {
      state.resources = {};
      state.resourcesIdsPerGroup = {};
      state.groups = [];
      state.groupsMeta = {};
    });
  },
});

export default makeIacTableDataSlice;
